import {
  type HttpEvent,
  type HttpHandlerFn,
  type HttpInterceptorFn,
  type HttpRequest,
} from '@angular/common/http';
import { catchError, from, type Observable, switchMap, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { ERROR_HANDLER_INJECTION_TOKEN } from './error-handler.token';
import type { CappusErrorHandler } from './cappus-error-handler';
import type { CappusError } from './error/cappus-error';
import { ERROR_CONTEXT } from './error-context.token';
import type { CappusErrorContext } from './cappus-error-context';
import { parseBackendError } from './error/parse-backend-error';
import type { CappusBackendError } from './error/cappus-backend-error';
import { normalizeHttpError } from './normalize-http-error.operator';

async function handleError(
  handlers: CappusErrorHandler[],
  beError: CappusBackendError,
): Promise<CappusError> {
  const error = parseBackendError(beError);
  await execErrorHandlers(handlers, error);
  return error;
}

async function execErrorHandlers(
  handlers: CappusErrorHandler[],
  error: CappusError,
): Promise<void> {
  for (const handler of handlers) {
    if (handler.canHandle(error)) {
      await handler.handle(error);
      break;
    }
  }
}

export const httpErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const handlers: CappusErrorHandler[] = inject(ERROR_HANDLER_INJECTION_TOKEN);
  const context = req.context.get<CappusErrorContext>(ERROR_CONTEXT);

  if (context.skipGlobalHandlers) {
    return next(req);
  }

  return next(req).pipe(
    normalizeHttpError(),
    catchError(
      (beError: CappusBackendError): Observable<HttpEvent<unknown>> =>
        from(handleError(handlers, beError)).pipe(switchMap(error => throwError(() => error))),
    ),
  );
};
